import axios, { type AxiosError } from 'axios'
import { useStorage } from '@vueuse/core'

import routes from '@core/modules/Auth/routes'
import { type CustomAxiosRequestConfig } from '@services/api/declarations'
// ***
// COMMON
// ***

const storage = useStorage('cluber', {
  auth: { token: '' },
}, localStorage)

// ***
// AXIOS INSTANCE
// ***

export const axiosInstance = axios.create()

// ***
// HANDLE NOT AUTHORIZED ERROR
// ***

function handleNotAuthorizedError(error: unknown) {
  const authRoutes = {
    authLogout: `${routes.basePath}${routes.routes.find((route) => route.name === 'authLogout')?.path}`,
    authLogin: `${routes.basePath}${routes.routes.find((route) => route.name === 'authLogin')?.path}`,
  }
  const axiosError = error as AxiosError
  if (axiosError?.response?.status === 401) {
    // Do not redirect to login if user is recovering password
    if (window.location.pathname?.includes('/password/recover')) return

    // Don't redirect if user is trying to login
    if (!axiosInstance.defaults.headers.common.Authorization) {
      if (window.location.pathname === '/auth/login') return
    }

    const path = window.location.pathname
    const notAllowedRoutes = ['login', 'logout', 'profile-selector'].some(item => path.includes(item))
    const urlQueryParams = new URLSearchParams(window.location.search)
    const isROOT = (urlQueryParams.get('from') === '/') || (path === '/')

    const queryParams = new URLSearchParams()
    queryParams.set('expiredToken', 'true')

    if (!isROOT || !notAllowedRoutes) {
      const fromParam = urlQueryParams.get('from') || path
      queryParams.set('from', fromParam)
      queryParams.set('keepCurrentUrl', 'true')
    }

    if (!path.includes('recover') && path !== authRoutes.authLogin) {
      window.location.href = `${authRoutes.authLogout}?${queryParams.toString()}`
    }
  }
}

// ***
// AXIOS INTERCEPTORS
// ***

axiosInstance.interceptors.request.use((config: CustomAxiosRequestConfig) => {
  const token = storage.value?.auth.token
  if (token && !config.skipToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    handleNotAuthorizedError(error)
    return Promise.reject(error)
  },
)
